<script setup>
import {inject, onMounted, ref} from "vue";
import Draggable from 'vuedraggable'
import {cloneArray} from "@/helpers/array";
import constants from "@/constants/constants";
import PulseLoader from "vue-spinner/src/MoonLoader.vue";
import useStoreTrigger from "@/composables/useStoreTrigger";
import ScrolledBottom from "@/components/ScrolledBottom.vue";
import Loader from "@/components/ui/Loader.vue";

const emit = defineEmits(['countChanged']);
const storeTrigger = useStoreTrigger();

const getStagesDataMethod = inject("getStagesData");
const getStageItemsMethod = inject("getStageItems");
const saveFunnelChangesMethod = inject("saveFunnelChanges");

const stagesData = ref([]);
const initStagesData = ref([]);
const header = ref(null);
const stickHeader = ref(false);
const loading = ref(false);
const lastLinkId = ref(0);

onMounted(() => {
    reset();
});

async function reset() {
    const {data} = await getStagesDataMethod();

    stagesData.value = [];
    let totalCount = 0;

    data.forEach(stage => {
        stagesData.value.push({
            ...stage,
            items: [],
            loading: false,
        })

        totalCount += stage.count;
        getStageItems(stage.id);
    })

    emit("countChanged", totalCount);

    initStagesData.value = cloneArray(stagesData.value);
    loading.value = false;
}

async function getStageItems(stageId) {
    let index = stagesData.value.findIndex(item => item.id === stageId);

    if (!stagesData.value[index].loading && stagesData.value[index].items.length < stagesData.value[index].count) {
        stagesData.value[index].loading = true;

        let stageLastLinkId = stagesData.value[index].items.at(-1)?.link_id || 0;
        const {data} = await getStageItemsMethod(stageId, stageLastLinkId);

        stagesData.value[index].items.push(...data.filter(oldItem => !stagesData.value[index].items.some(newItem => newItem.id === oldItem.id)));

        if (stagesData.value[index].items.length && stagesData.value[index].items[0].link_id > lastLinkId.value) {
            lastLinkId.value = stagesData.value[index].items[0].link_id;
        }

        initStagesData.value = cloneArray(stagesData.value);
        stagesData.value[index].loading = false;
    }
}

async function updateStagesData() {
    const {data} = await getStagesDataMethod();

    data.forEach(stage => {
        let index = stagesData.value.findIndex(item => item.id === stage.id);

        stagesData.value[index].count = stage.count;
    })
}

function loadMore(index) {
    getStageItems(stagesData.value[index].id);
}

async function onItemStageChange(e) {
    if ('added' in e) {
        let changes = [];
        let sendEmail = false;

        for (let stage in stagesData.value) {
            let initStage = initStagesData.value[stage]
            let changedItems = []

            stagesData.value[stage].items.forEach(newItem => {
                if (!initStage.items.find(oldItem => oldItem.id === newItem.id)) {
                    changedItems.push(newItem.id)
                    sendEmail = stagesData.value[stage].send_email;
                }
            })

            if (changedItems.length) {
                changes.push({
                    id: initStage.id,
                    items: changedItems,
                })
            }
        }

        if (changes.length) {
            if (!sendEmail || confirm("Startup will be notified about stage change. Would you like to proceed?")) {
                loading.value = true;

                const {data: updatedItems} = await saveFunnelChangesMethod(changes);

                for (let stageId in updatedItems) {
                    let stage = stagesData.value.find(item => parseInt(item.id) === parseInt(stageId));

                    for (let itemId in updatedItems[stageId]) {
                        let itemIndex = stage.items.findIndex(item => parseInt(item.id) === parseInt(itemId));
                        stage.items[itemIndex] = updatedItems[stageId][itemId];
                    }
                }

                loading.value = false;

                updateStagesData();
                storeTrigger('setNeedUpdateTrackerLogs');

                // ensure Vue detects nested updates after move/update
                stagesData.value = stagesData.value.map(s => ({ ...s, items: [...s.items] }));
            } else {
                stagesData.value = cloneArray(initStagesData.value);
            }
        }
    }
}

function rearrangePinned(itemId) {
    let stageIndex = stagesData.value.findIndex(stage => stage.items.some(item => item.id === itemId));

    stagesData.value[stageIndex].items = [
        ...stagesData.value[stageIndex].items.filter(item => item.pinned),
        ...stagesData.value[stageIndex].items.filter(item => !item.pinned)
    ];
}

defineExpose({
    reset,
    rearrangePinned,
});
</script>

<template>
    <div
        ref="header"
        class="content-tracker-board"
    >
        <PulseLoader :loading="loading" :color="constants.spinnerColor"/>

        <div class="content-tracker-board__header" :class="{'sticky-header': stickHeader}">
            <header
                v-for="(stage, stageIndex) in stagesData"
                :key="stageIndex"
                class="content-tracker-board__title"
            >
                {{ stage.name }} <span class="startups-cnt">({{ stage.count }})</span>
            </header>
        </div>

        <div ref="right-container" class="content-tracker-board__main-wrapper">
            <div class="content-tracker-board__main">
                <div v-for="(stage, stageIndex) in stagesData" :key="stageIndex"
                     class="content-tracker-board__column" :class="'column-' + stage.id">

                    <Draggable
                        v-model="stagesData[stageIndex].items"
                        handle=".draggable"
                        class="content-tracker-board__content"
                        group="items"
                        @change="onItemStageChange"
                    >
                        <div v-for="item in stagesData[stageIndex].items" :key="item.id">
                            <slot name="item" :item="item"/>
                        </div>

                        <ScrolledBottom @scrolledBottom = loadMore(stageIndex) />
                        <Loader class="mb-1" v-if="stagesData[stageIndex].loading"/>

                        <slot name="addLink" :stageId="stage.id"/>
                    </Draggable>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>
